import { AxiosResponse } from 'axios';
import isEqual from 'lodash.isequal';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { all, put, select } from 'redux-saga/effects';
import { RootState } from 'src/app/store';
import instance, { CustomAxiosError } from 'src/axios';
import { apiBaseRoutes, apiRoutes } from 'src/constants/apiConstants';

import { Errors } from '../../constants/errorConstants';
import {
  addFile,
  addSnackbar,
  editDynamicSettings,
  getDynamicSettings,
  getFileUrl,
  setDynamicSettings,
  startLoading,
  stopLoading,
} from '../../features/globalSlice';
import { FileResponse } from '../models/Action';
import { SnackbarType } from '../models/Snackbar';
import { WhiteLabelConfig } from '../models/WhiteLabel';
import { ApiResponse } from '.';

/* Get file URL */
export function* GlobalGetFileURLSaga(
  action: ReturnType<typeof getFileUrl>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type, id: action.payload?.id }));
  try {
    const formData = new FormData();
    formData.append('file', action.payload?.file as File);

    const response = (yield instance.post(
      `${apiBaseRoutes.UPLOADS}${apiRoutes.FILE}`,
      formData
    )) as AxiosResponse<ApiResponse<FileResponse>>;

    yield put(
      addFile({
        id: action.payload?.id,
        url: encodeURI(response?.data?.data?.url),
        fileName: action.payload?.file?.name,
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type, id: action.payload?.id }));
  }
}

/* Edit dynamic settings */
export function* GlobalEditDynamicSettingsSaga(
  whiteLabelsConfig: WhiteLabelConfig[]
): Generator {
  yield put(startLoading({ actionType: editDynamicSettings.type }));
  const state = (yield select()) as RootState;
  try {
    const changedLabels = whiteLabelsConfig?.filter(
      (l) =>
        !isEqual(
          l,
          state?.global?.dynamicSettings?.whiteLabelsConfig?.find(
            (o) => o?.appName === l?.appName
          )
        )
    );
    yield all(
      changedLabels?.map((s) =>
        instance.post(`${apiBaseRoutes.CONFIG}/${s?.appName}`, {
          maxOpenTickets: s?.value?.maxOpenTickets,
          showManufacturers: s?.value?.showManufacturers,
        })
      )
    );

    yield put(setDynamicSettings({ whiteLabelsConfig }));
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(stopLoading({ actionType: editDynamicSettings.type }));
  }
}

/* Get dynamic settings */
export function* GlobalGetDynamicSettingsSaga(): Generator {
  yield put(startLoading({ actionType: getDynamicSettings.type }));
  try {
    const response = (yield instance.get(
      `${apiBaseRoutes.CONFIG}${apiRoutes.LIST}${apiRoutes.ALL}`
    )) as AxiosResponse<ApiResponse<WhiteLabelConfig[]>>;

    yield put(
      setDynamicSettings({
        whiteLabelsConfig: response?.data?.data?.filter((w) => w?.appName),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(stopLoading({ actionType: getDynamicSettings.type }));
  }
}
